import React, { useRef, useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useCookies } from 'react-cookie'

import SaveService from 'services/save.service'

import { store } from 'redux/store'

import { useActionless, useAppSelector } from 'redux/hooks'
import { useCanEditDeck } from 'redux/deck/selectors'
import { useRightPanel } from 'hooks/usePinnedSidecar'
import { SET_ACTIVE_STATE, TOGGLE_MODAL } from 'redux/active/actions/types'

import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import SaveWatcher from 'components/deckPage/DeckContainerV2/SaveWatcher'
import Icon from 'components/elements/Icon'

import styles from './mobileToolbar.module.scss'

export const BOTTOM_TOOLBAR_VISABLE_CLASSNAME = 'bottom-toolbar-visable'

// mobile deck toolbar, hides itself via css on desktop
const MobileToolbar = () => {
  const router = useRouter()

  const canEditDeck = useCanEditDeck()

  const [hiddenFromScroll, setHiddenFromScroll] = useState(false)

  const stoppedPosition = useRef<number>(0)
  const scrollPosition = useRef<number>(0)

  const scrollListener = useRef<(e: Event) => void>(() => null)
  const scrollSetterTimeout = useRef<ReturnType<typeof setTimeout>>()

  const [{ tbId: userId }] = useCookies(['tbId'])

  const undoStack = useAppSelector(state => state.deck.actionStack)
  const redoStack = useAppSelector(state => state.deck.undoneActionStack)

  const deckId = useAppSelector(state => state.deck.id)
  const parentFolder = useAppSelector(state => state.deck.parentFolder)
  const ownerId = useAppSelector(state => state.deck.ownerid)
  const snapshotMeta = useAppSelector(state => state.deck.snapshotMeta)

  const [rightPanelStatus, setRightPanelStatus] = useRightPanel()

  const [toggleModal, setActiveState] = useActionless(TOGGLE_MODAL, SET_ACTIVE_STATE)

  const handleHide = (flag: boolean) => {
    setHiddenFromScroll(flag)

    if (flag) return document.body.classList.remove(BOTTOM_TOOLBAR_VISABLE_CLASSNAME)
    else document.body.classList.add(BOTTOM_TOOLBAR_VISABLE_CLASSNAME)
  }

  useEffect(() => {
    handleHide(false)

    return () => {
      document.body.classList.remove(BOTTOM_TOOLBAR_VISABLE_CLASSNAME)
    }
  }, [])

  useEffect(() => {
    window.removeEventListener('scroll', scrollListener.current)

    scrollListener.current = (_e: Event) => {
      const pixelSlop = 3
      const scrollFromTop = document.documentElement.scrollTop

      const scrollingDown = scrollPosition.current < scrollFromTop + pixelSlop
      scrollPosition.current = scrollFromTop >= 0 ? scrollFromTop : 0 // used To tell the scroll direction

      if (scrollSetterTimeout.current) clearTimeout(scrollSetterTimeout.current)

      scrollSetterTimeout.current = setTimeout(() => {
        stoppedPosition.current = scrollFromTop >= 0 ? scrollFromTop : 0
      }, 50)

      // These are the states we already want, if these are set leave them alone
      if (scrollingDown && hiddenFromScroll) return
      if (!scrollingDown && !hiddenFromScroll) return

      if (scrollingDown && scrollPosition.current - pixelSlop > stoppedPosition.current) handleHide(true)
      if (!scrollingDown && scrollPosition.current + pixelSlop < stoppedPosition.current) handleHide(false)
    }

    window.addEventListener('scroll', scrollListener.current)

    return () => window.removeEventListener('scroll', scrollListener.current)
  }, [hiddenFromScroll])

  const ownsDeck = Number(userId) === Number(ownerId)

  return (
    <div className={`${styles.bar} ${hiddenFromScroll ? styles.hidden : ''}`}>
      <button onClick={() => setActiveState({ searchOpen: 'search' })} className={styles.button}>
        <Icon name="search" />

        <label>Search</label>
      </button>

      {canEditDeck && (
        <>
          <SaveWatcher className={styles.button} includeLabel />
          <button
            onClick={() => SaveService.undo()}
            className={styles.button}
            disabled={!canEditDeck || !undoStack.length}>
            <Icon name="undo" />

            <label>Undo</label>
          </button>
          <button
            onClick={() => SaveService.redo()}
            className={styles.button}
            disabled={!canEditDeck || !redoStack.length}>
            <Icon name="redo" />
            <label>Redo</label>
          </button>
        </>
      )}

      {!canEditDeck && (
        <>
          <button
            onClick={() =>
              !userId
                ? router.push(`/login?redirectTo=${encodeURIComponent(router.asPath)}`)
                : toggleModal({ modalName: 'cloneModal', flag: true })
            }
            className={styles.button}>
            <Icon name="clone" />

            <label>Clone</label>
          </button>

          <button onClick={() => setActiveState({ interactionOverlay: true })} className={styles.button}>
            <Icon name="comment outline" />
            <label>Description</label>
          </button>

          <button onClick={() => router.push(`/playtester-v2/${deckId}`)} className={styles.button}>
            <Icon name="play circle outline" />
            <label>Playtest</label>
          </button>
        </>
      )}

      <ArchidektDropdown
        options={[
          {
            label: 'Import cards',
            id: 'import',
            icon: 'edit outline',
            onClick: () => toggleModal({ modalName: 'massEditModal', flag: true }),
            hidden: !canEditDeck,
          },
          {
            label: 'Export deck',
            id: 'export',
            onClick: () => toggleModal({ modalName: 'export', flag: true }),
            icon: 'download',
          },
          { type: 'spacer' },
          {
            label: 'Clone deck',
            id: 'clone',
            onClick: () => toggleModal({ modalName: 'cloneModal', flag: true }),
            icon: 'clone',
            hidden: !userId,
          },
          {
            label: 'Card packages',
            id: 'packages',
            icon: 'cube',
            onClick: () => toggleModal({ modalName: 'cardPackageModal', flag: true }),
          },
          {
            label: 'Add deck to collection',
            id: 'addToCollection',
            onClick: () => toggleModal({ modalName: 'addToCollection', flag: true }),
            icon: 'plus circle',
            hidden: !userId,
          },
          { type: 'spacer' },
          {
            label: 'Select all',
            id: 'selectAll',
            keybind: 'Ctrl + A',
            onClick: () => {
              const { cardMap } = store.getState().deck
              const multiSelectedIds = Object.keys(cardMap).reduce((a, id) => ({ ...a, [id]: true }), {})

              setActiveState({ multiSelectedIds })
            },
            icon: 'hand pointer outline',
          },
          {
            label: 'Optimize printings',
            id: 'optimize',
            onClick: () => {
              const { cardMap } = store.getState().deck
              const multiSelectedIds = Object.keys(cardMap).reduce((a, id) => ({ ...a, [id]: true }), {})

              setActiveState({ multiSelectedIds, optimizeCardOverlay: true })
            },
            icon: 'money bill alternate outline',
          },
          {
            label: 'Basic land optimizer',
            id: 'lands',
            icon: 'fire',
            onClick: () => setActiveState({ autofillBasicLandsOverlay: true }),
          },
          {
            label: 'Add custom cards',
            icon: 'file image outline',
            hidden: `${userId}` !== `${ownerId}` || !!snapshotMeta,
            onClick: () => setActiveState({ selectCustomCards: true }),
          },
          {
            label: 'Deck charts',
            icon: 'pie graph',
            onClick: () => setRightPanelStatus({ ...rightPanelStatus, open: true }),
          },
          {
            label: 'Description & comments',
            icon: 'comment outline',
            onClick: () => setActiveState({ interactionOverlay: true }),
          },
          {
            label: 'Open deck options',
            icon: 'arrow circle right',
            type: 'extras',
            id: 'deck-options-cont',
            options: [
              {
                label: 'Open containing folder',
                to: `/folders/${parentFolder}`,
                icon: 'folder open',
                hidden: !ownsDeck,
              },
              {
                label: 'Open in sandbox',
                id: 'sandbox',
                icon: 'flask',
                to: `/sandbox/${deckId}`,
              },
            ],
          },
          { type: 'spacer' },
          {
            label: 'Deck history',
            id: 'history',
            onClick: () => toggleModal({ modalName: 'addRemovedCards', flag: true }),
            icon: 'history',
          },
          {
            label: 'Deck settings',
            id: 'settings',
            onClick: () => toggleModal({ modalName: 'deckSettings', flag: true }),
            icon: 'cog',
            disabled: !canEditDeck,
          },
        ]}
        direction="up"
        triggerClassName={styles.extrasTrigger}
        menuClassName={styles.extrasMenu}>
        <button className={styles.button}>
          <Icon name="ellipsis horizontal" />

          <label>Extras</label>
        </button>
      </ArchidektDropdown>
    </div>
  )
}

export default MobileToolbar
